import {
  RoadNodeDto,
  RoadSegmentDirection,
  RoadSegmentDto,
} from '@yard/features/road-network/models/road-network.model'
import { RoadNetworkStore } from '@yard/stores/road-network.store'
import { action, computed, makeObservable, observable, runInAction } from 'mobx'
import { TempIdResolver, generateTempId, isTempId } from './temp-id-resolver'

export type ContextMenuTargetType = 'node' | 'segment'

export class RoadNetworkViewStore {
  isDrawingMode = false
  pendingNodeId: string | null = null

  isContextMenuOpen = false
  contextMenuTargetId: string | null = null
  contextMenuTargetType: ContextMenuTargetType | null = null
  contextMenuPosition: { x: number; y: number } | null = null

  tempIdResolver = new TempIdResolver()
  lastSplitTime = 0

  constructor(private readonly roadNetworkStore: RoadNetworkStore) {
    makeObservable(this, {
      isDrawingMode: observable,
      pendingNodeId: observable,
      isContextMenuOpen: observable,
      contextMenuTargetId: observable,
      contextMenuTargetType: observable,
      contextMenuPosition: observable,

      nodes: computed,
      segments: computed,
      pendingNode: computed,

      toggleDrawingMode: action,
      exitDrawingMode: action,
      clearPendingNode: action,
      placeNode: action,
      connectToNode: action,
      splitSegment: action,
      openContextMenu: action,
      closeContextMenu: action,
    })
  }

  get nodes(): RoadNodeDto[] {
    return this.roadNetworkStore.nodes
  }

  get segments(): RoadSegmentDto[] {
    return this.roadNetworkStore.segments
  }

  get pendingNode(): RoadNodeDto | null | undefined {
    const pendingNode = this.pendingNodeId
      ? this.nodes.find(n => n.id === this.pendingNodeId)
      : null

    return pendingNode
  }

  toggleDrawingMode = () => {
    if (this.isDrawingMode) {
      this.exitDrawingMode()
    } else {
      this.isDrawingMode = true
    }
  }

  exitDrawingMode = () => {
    this.isDrawingMode = false
    this.pendingNodeId = null
  }

  clearPendingNode = () => {
    this.pendingNodeId = null
  }

  openContextMenu = (
    targetId: string,
    targetType: ContextMenuTargetType,
    position: { x: number; y: number },
  ) => {
    this.isContextMenuOpen = true
    this.contextMenuTargetId = targetId
    this.contextMenuTargetType = targetType
    this.contextMenuPosition = position
  }

  closeContextMenu = () => {
    this.isContextMenuOpen = false
    this.contextMenuTargetId = null
    this.contextMenuTargetType = null
    this.contextMenuPosition = null
  }

  placeNode = (
    position: { x: number; y: number },
    layoutObjectId?: string | null,
    blockAccessPointId?: string | null,
  ) => {
    const tempNodeId = generateTempId()
    const previousPendingNodeId = this.pendingNodeId

    const optimisticNode: RoadNodeDto = {
      id: tempNodeId,
      name: tempNodeId,
      layoutPosition: { x: Math.round(position.x), y: Math.round(position.y), rotation: 0 },
      type: 'Intersection',
      layoutObjectId: layoutObjectId ?? null,
      blockAccessPointId: blockAccessPointId ?? null,
    }

    this.tempIdResolver.register(tempNodeId)
    this.roadNetworkStore.addNodeOptimistic(optimisticNode)

    let tempSegmentId: string | null = null
    if (previousPendingNodeId) {
      tempSegmentId = generateTempId()
      this.tempIdResolver.register(tempSegmentId)
      this.roadNetworkStore.addSegmentOptimistic({
        id: tempSegmentId,
        startNodeId: previousPendingNodeId,
        endNodeId: tempNodeId,
        direction: 'TwoWay',
        isBlocked: false,
      })
    }

    this.pendingNodeId = tempNodeId

    this.roadNetworkStore
      .createNode({
        name: tempNodeId,
        layoutPosition: { x: Math.round(position.x), y: Math.round(position.y), rotation: 0 },
        type: 'Intersection',
        layoutObjectId,
        blockAccessPointId,
      })
      .then(realNode => {
        this.tempIdResolver.resolve(tempNodeId, realNode.id)
        runInAction(() => {
          this.roadNetworkStore.replaceNodeId(tempNodeId, realNode)
          if (this.pendingNodeId === tempNodeId) {
            this.pendingNodeId = realNode.id
          }
        })
      })
      .catch(() => {
        this.tempIdResolver.reject(tempNodeId, new Error('Node creation failed'))
        runInAction(() => {
          this.roadNetworkStore.removeNodeOptimistic(tempNodeId)
          if (this.pendingNodeId === tempNodeId) {
            this.pendingNodeId = previousPendingNodeId
          }
        })
      })

    if (tempSegmentId && previousPendingNodeId) {
      const segTempId = tempSegmentId
      Promise.all([
        this.tempIdResolver.waitForReal(previousPendingNodeId),
        this.tempIdResolver.waitForReal(tempNodeId),
      ])
        .then(([realStartId, realEndId]) =>
          this.roadNetworkStore.createSegment({
            startNodeId: realStartId,
            endNodeId: realEndId,
            direction: 'TwoWay',
            isBlocked: false,
          }),
        )
        .then(realSegment => {
          this.tempIdResolver.resolve(segTempId, realSegment.id)
          runInAction(() => {
            this.roadNetworkStore.replaceSegmentId(segTempId, realSegment)
          })
        })
        .catch(() => {
          runInAction(() => {
            this.roadNetworkStore.removeSegmentOptimistic(segTempId)
          })
        })
    }
  }

  splitSegment = async (segmentId: string, position: { x: number; y: number }) => {
    const now = Date.now()
    if (now - this.lastSplitTime < 300) return
    this.lastSplitTime = now

    const segment = this.roadNetworkStore.segments.find(s => s.id === segmentId)
    if (!segment) return

    const previousPendingNodeId = this.pendingNodeId
    const hasPending =
      previousPendingNodeId &&
      previousPendingNodeId !== segment.startNodeId &&
      previousPendingNodeId !== segment.endNodeId

    const newNode = await this.roadNetworkStore.createNode({
      name: crypto.randomUUID(),
      layoutPosition: { x: Math.round(position.x), y: Math.round(position.y), rotation: 0 },
      type: 'Intersection',
    })

    const inheritedProps = {
      direction: segment.direction,
      isBlocked: segment.isBlocked,
      weightLimit: segment.weightLimit,
    }

    const tempSegIds: string[] = [crypto.randomUUID(), crypto.randomUUID()]
    if (hasPending) tempSegIds.push(crypto.randomUUID())

    const optimisticSegments: RoadSegmentDto[] = [
      {
        id: tempSegIds[0],
        startNodeId: segment.startNodeId,
        endNodeId: newNode.id,
        ...inheritedProps,
      },
      {
        id: tempSegIds[1],
        startNodeId: newNode.id,
        endNodeId: segment.endNodeId,
        ...inheritedProps,
      },
    ]
    if (hasPending) {
      optimisticSegments.push({
        id: tempSegIds[2],
        startNodeId: previousPendingNodeId!,
        endNodeId: newNode.id,
        direction: 'TwoWay',
        isBlocked: false,
      })
    }

    runInAction(() => {
      if (!this.roadNetworkStore.nodes.some(n => n.id === newNode.id)) {
        this.roadNetworkStore.nodes = [...this.roadNetworkStore.nodes, newNode]
      }
      this.roadNetworkStore.segments = [
        ...this.roadNetworkStore.segments.filter(s => s.id !== segmentId),
        ...optimisticSegments,
      ]
      this.pendingNodeId = newNode.id
    })

    // Resolve pending node ID for the connection segment if it was temp
    const resolvedPendingId = hasPending
      ? await this.tempIdResolver.waitForReal(previousPendingNodeId!).catch(() => null)
      : null

    // Persist to backend in parallel
    const segmentCalls: Promise<RoadSegmentDto>[] = [
      this.roadNetworkStore.createSegment({
        startNodeId: segment.startNodeId,
        endNodeId: newNode.id,
        ...inheritedProps,
      }),
      this.roadNetworkStore.createSegment({
        startNodeId: newNode.id,
        endNodeId: segment.endNodeId,
        ...inheritedProps,
      }),
    ]
    if (hasPending && resolvedPendingId) {
      segmentCalls.push(
        this.roadNetworkStore.createSegment({
          startNodeId: resolvedPendingId,
          endNodeId: newNode.id,
          direction: 'TwoWay',
          isBlocked: false,
        }),
      )
    }

    const [realSegments] = await Promise.all([
      Promise.all(segmentCalls),
      this.roadNetworkStore.deleteSegment(segmentId),
    ])

    // Replace temp segments with real ones atomically
    runInAction(() => {
      let segments = this.roadNetworkStore.segments.filter(s => !tempSegIds.includes(s.id))
      for (const real of realSegments) {
        if (!segments.some(s => s.id === real.id)) {
          segments = [...segments, real]
        }
      }
      this.roadNetworkStore.segments = segments
    })
  }

  connectToNode = (nodeId: string) => {
    if (!this.pendingNodeId || this.pendingNodeId === nodeId) {
      this.pendingNodeId = nodeId
      return
    }

    const previousPendingNodeId = this.pendingNodeId
    const tempSegmentId = generateTempId()
    this.tempIdResolver.register(tempSegmentId)

    this.roadNetworkStore.addSegmentOptimistic({
      id: tempSegmentId,
      startNodeId: previousPendingNodeId,
      endNodeId: nodeId,
      direction: 'TwoWay',
      isBlocked: false,
    })

    this.pendingNodeId = nodeId

    Promise.all([
      this.tempIdResolver.waitForReal(previousPendingNodeId),
      this.tempIdResolver.waitForReal(nodeId),
    ])
      .then(([realStartId, realEndId]) =>
        this.roadNetworkStore.createSegment({
          startNodeId: realStartId,
          endNodeId: realEndId,
          direction: 'TwoWay',
          isBlocked: false,
        }),
      )
      .then(realSegment => {
        this.tempIdResolver.resolve(tempSegmentId, realSegment.id)
        runInAction(() => {
          this.roadNetworkStore.replaceSegmentId(tempSegmentId, realSegment)
        })
      })
      .catch(() => {
        runInAction(() => {
          this.roadNetworkStore.removeSegmentOptimistic(tempSegmentId)
        })
      })
  }

  debouncedSetNodePosition = (id: string, position: { x: number; y: number }) => {
    const node = this.roadNetworkStore.nodes.find(n => n.id === id)
    if (!node) return
    const newX = Math.round(position.x)
    const newY = Math.round(position.y)
    if (node.layoutPosition.x === newX && node.layoutPosition.y === newY) return

    const updatedNode = { ...node, layoutPosition: { ...node.layoutPosition, x: newX, y: newY } }
    this.roadNetworkStore.updateNodeOptimistic(updatedNode)
    this.roadNetworkStore.debouncedUpdateNode(id)
  }

  setNodePosition = async (id: string, position: { x: number; y: number }) => {
    const node = this.roadNetworkStore.nodes.find(n => n.id === id)
    if (!node) return
    const newX = Math.round(position.x)
    const newY = Math.round(position.y)
    if (node.layoutPosition.x === newX && node.layoutPosition.y === newY) return

    const updatedNode = { ...node, layoutPosition: { ...node.layoutPosition, x: newX, y: newY } }
    const previousNode = { ...node }

    this.roadNetworkStore.updateNodeOptimistic(updatedNode)

    try {
      const realId = await this.tempIdResolver.waitForReal(id)
      await this.roadNetworkStore.updateNode({ ...updatedNode, id: realId })
    } catch {
      runInAction(() => {
        this.roadNetworkStore.updateNodeOptimistic(previousNode)
      })
    }
  }

  updateSegmentDirection = async (id: string, direction: RoadSegmentDirection) => {
    const segment = this.roadNetworkStore.segments.find(s => s.id === id)
    if (!segment) return

    const previousSegment = { ...segment }
    this.roadNetworkStore.updateSegmentOptimistic({ ...segment, direction })

    try {
      const [realId, realStartId, realEndId] = await Promise.all([
        this.tempIdResolver.waitForReal(id),
        this.tempIdResolver.waitForReal(segment.startNodeId),
        this.tempIdResolver.waitForReal(segment.endNodeId),
      ])
      await this.roadNetworkStore.updateSegment({
        ...segment,
        id: realId,
        startNodeId: realStartId,
        endNodeId: realEndId,
        direction,
      })
    } catch {
      runInAction(() => {
        this.roadNetworkStore.updateSegmentOptimistic(previousSegment)
      })
    }
  }

  updateSegmentBlocked = async (id: string, isBlocked: boolean) => {
    const segment = this.roadNetworkStore.segments.find(s => s.id === id)
    if (!segment) return

    const previousSegment = { ...segment }
    this.roadNetworkStore.updateSegmentOptimistic({ ...segment, isBlocked })

    try {
      const [realId, realStartId, realEndId] = await Promise.all([
        this.tempIdResolver.waitForReal(id),
        this.tempIdResolver.waitForReal(segment.startNodeId),
        this.tempIdResolver.waitForReal(segment.endNodeId),
      ])
      await this.roadNetworkStore.updateSegment({
        ...segment,
        id: realId,
        startNodeId: realStartId,
        endNodeId: realEndId,
        isBlocked,
      })
    } catch {
      runInAction(() => {
        this.roadNetworkStore.updateSegmentOptimistic(previousSegment)
      })
    }
  }

  updateSegmentWeightLimit = async (id: string, weightLimit?: number) => {
    const segment = this.roadNetworkStore.segments.find(s => s.id === id)
    if (!segment) return

    const previousSegment = { ...segment }
    this.roadNetworkStore.updateSegmentOptimistic({ ...segment, weightLimit })

    try {
      const [realId, realStartId, realEndId] = await Promise.all([
        this.tempIdResolver.waitForReal(id),
        this.tempIdResolver.waitForReal(segment.startNodeId),
        this.tempIdResolver.waitForReal(segment.endNodeId),
      ])
      await this.roadNetworkStore.updateSegment({
        ...segment,
        id: realId,
        startNodeId: realStartId,
        endNodeId: realEndId,
        weightLimit,
      })
    } catch {
      runInAction(() => {
        this.roadNetworkStore.updateSegmentOptimistic(previousSegment)
      })
    }
  }

  deleteSegment = async (id: string) => {
    if (isTempId(id)) {
      this.roadNetworkStore.removeSegmentOptimistic(id)
      this.tempIdResolver.reject(id, new Error('Deleted before creation'))
      this.closeContextMenu()
      return
    }

    const segment = this.roadNetworkStore.segments.find(s => s.id === id)
    const remainingSegments = this.roadNetworkStore.segments.filter(s => s.id !== id)

    const orphanedNodes: RoadNodeDto[] = segment
      ? [segment.startNodeId, segment.endNodeId]
          .map(nId => this.roadNetworkStore.nodes.find(n => n.id === nId))
          .filter((n): n is RoadNodeDto => !!n)
          .filter(n => !remainingSegments.some(s => s.startNodeId === n.id || s.endNodeId === n.id))
      : []

    this.roadNetworkStore.removeSegmentOptimistic(id)
    orphanedNodes.forEach(n => this.roadNetworkStore.removeNodeOptimistic(n.id))
    this.closeContextMenu()

    try {
      await this.roadNetworkStore.deleteSegment(id)
      await Promise.all(orphanedNodes.map(n => this.roadNetworkStore.deleteNode(n.id)))
    } catch {
      runInAction(() => {
        if (segment) this.roadNetworkStore.addSegmentOptimistic(segment)
        orphanedNodes.forEach(n => this.roadNetworkStore.addNodeOptimistic(n))
      })
    }
  }

  deleteNode = async (id: string) => {
    if (isTempId(id)) {
      this.roadNetworkStore.removeNodeOptimistic(id)
      this.tempIdResolver.reject(id, new Error('Deleted before creation'))
      this.closeContextMenu()
      return
    }

    const node = this.roadNetworkStore.nodes.find(n => n.id === id)
    const affectedSegments = this.roadNetworkStore.segments.filter(
      s => s.startNodeId === id || s.endNodeId === id,
    )
    const otherNodeIds = [
      ...new Set(
        affectedSegments.flatMap(s => [s.startNodeId, s.endNodeId]).filter(nId => nId !== id),
      ),
    ]

    const hasExistingSegmentBetweenOthers =
      otherNodeIds.length === 2 &&
      this.roadNetworkStore.segments.some(
        s =>
          (s.startNodeId === otherNodeIds[0] && s.endNodeId === otherNodeIds[1]) ||
          (s.startNodeId === otherNodeIds[1] && s.endNodeId === otherNodeIds[0]),
      )

    const isReconnectCase =
      affectedSegments.length === 2 &&
      otherNodeIds.length === 2 &&
      !isTempId(otherNodeIds[0]) &&
      !isTempId(otherNodeIds[1]) &&
      !hasExistingSegmentBetweenOthers

    if (isReconnectCase) {
      const [nodeAId, nodeCId] = otherNodeIds
      const direction = inferReconnectDirection(nodeAId, id, nodeCId, affectedSegments)

      const tempSegmentId = generateTempId()
      this.tempIdResolver.register(tempSegmentId)

      this.roadNetworkStore.removeNodeOptimistic(id)
      this.roadNetworkStore.addSegmentOptimistic({
        id: tempSegmentId,
        startNodeId: nodeAId,
        endNodeId: nodeCId,
        direction,
        isBlocked: false,
      })
      this.closeContextMenu()

      try {
        await this.roadNetworkStore.deleteNode(id)

        const realSegment = await this.roadNetworkStore.createSegment({
          startNodeId: nodeAId,
          endNodeId: nodeCId,
          direction,
          isBlocked: false,
        })

        runInAction(() => {
          this.roadNetworkStore.replaceSegmentId(tempSegmentId, realSegment)
          this.tempIdResolver.resolve(tempSegmentId, realSegment.id)
        })
      } catch {
        runInAction(() => {
          if (node) this.roadNetworkStore.addNodeOptimistic(node)
          affectedSegments.forEach(s => this.roadNetworkStore.addSegmentOptimistic(s))
          this.roadNetworkStore.removeSegmentOptimistic(tempSegmentId)
          this.tempIdResolver.reject(tempSegmentId, new Error('Reconnect failed'))
        })
      }
      return
    }

    const remainingSegments = this.roadNetworkStore.segments.filter(
      s => s.startNodeId !== id && s.endNodeId !== id,
    )
    const orphanedNodes: RoadNodeDto[] = otherNodeIds
      .filter(nId => !remainingSegments.some(s => s.startNodeId === nId || s.endNodeId === nId))
      .map(nId => this.roadNetworkStore.nodes.find(n => n.id === nId))
      .filter((n): n is RoadNodeDto => !!n)

    this.roadNetworkStore.removeNodeOptimistic(id)
    orphanedNodes.forEach(n => this.roadNetworkStore.removeNodeOptimistic(n.id))
    this.closeContextMenu()

    try {
      await this.roadNetworkStore.deleteNode(id)
      await Promise.all(orphanedNodes.map(n => this.roadNetworkStore.deleteNode(n.id)))
    } catch {
      runInAction(() => {
        if (node) this.roadNetworkStore.addNodeOptimistic(node)
        affectedSegments.forEach(s => this.roadNetworkStore.addSegmentOptimistic(s))
        orphanedNodes.forEach(n => this.roadNetworkStore.addNodeOptimistic(n))
      })
    }
  }
}

function canTravel(segment: RoadSegmentDto, from: string, to: string): boolean {
  const { startNodeId: s, endNodeId: e, direction: d } = segment
  if (d === 'TwoWay') return (s === from && e === to) || (s === to && e === from)
  if (d === 'OneWayForward') return s === from && e === to
  return e === from && s === to
}

function inferReconnectDirection(
  nodeAId: string,
  nodeBId: string,
  nodeCId: string,
  segments: RoadSegmentDto[],
): RoadSegmentDirection {
  const segToA = segments.find(s => s.startNodeId === nodeAId || s.endNodeId === nodeAId)!
  const segToC = segments.find(s => s.startNodeId === nodeCId || s.endNodeId === nodeCId)!

  const canGoAtoC = canTravel(segToA, nodeAId, nodeBId) && canTravel(segToC, nodeBId, nodeCId)
  const canGoCtoA = canTravel(segToC, nodeCId, nodeBId) && canTravel(segToA, nodeBId, nodeAId)

  if (canGoAtoC && canGoCtoA) return 'TwoWay'
  if (canGoAtoC) return 'OneWayForward'
  if (canGoCtoA) return 'OneWayReverse'
  return 'TwoWay'
}
